import React from "react";
import {
    Box,
    Typography,
    Chip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import { ProductConversion } from "../../services/productConversionService";
import { formatCurrencyBDT } from "../../utils/currency";

const METHOD_LABELS: Record<string, string> = {
    SALES_VALUE: "By sales value",
    WEIGHT: "By net weight",
    MANUAL: "Manual percentage",
};

interface ConversionDetailContentProps {
    conversion: ProductConversion;
}

export const ConversionDetailContent: React.FC<ConversionDetailContentProps> = ({
    conversion,
}) => {
    const inputs = conversion.items.filter((i) => i.role === "INPUT");
    const outputs = conversion.items.filter((i) => i.role === "OUTPUT");
    // Decimal fields come back from the API as JSON strings (e.g.
    // "70.00") — Number() them before summing, since 0 + "70.00" is
    // string concatenation in JS, not addition.
    const inputTotal = inputs.reduce((sum, i) => sum + Number(i.total_cost), 0);
    const outputTotal = outputs.reduce((sum, i) => sum + Number(i.total_cost), 0);

    const renderTable = (rows: typeof inputs, showAllocation: boolean) => (
        <TableContainer component={Paper} variant="outlined">
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="center">Quantity</TableCell>
                        <TableCell align="center">Unit Cost</TableCell>
                        <TableCell align="center">Total Cost</TableCell>
                        {showAllocation && (
                            <TableCell align="center">Allocation %</TableCell>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((item) => (
                        <TableRow key={item.item_id}>
                            <TableCell>
                                <Typography variant="body2">
                                    {item.product?.name || item.product_id}
                                </Typography>
                                {item.product?.sku && (
                                    <Typography variant="caption" color="text.secondary">
                                        {item.product.sku}
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell align="center">{item.quantity}</TableCell>
                            <TableCell align="center">
                                {formatCurrencyBDT(item.unit_cost)}
                            </TableCell>
                            <TableCell align="center">
                                {formatCurrencyBDT(item.total_cost)}
                            </TableCell>
                            {showAllocation && (
                                <TableCell align="center">
                                    {item.allocation_percentage != null
                                        ? `${item.allocation_percentage}%`
                                        : "—"}
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="h6">{conversion.conversion_number}</Typography>
                    <Chip label={conversion.status} color="success" size="small" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {new Date(conversion.conversion_date).toLocaleString()}
                </Typography>
                {conversion.cost_allocation_method && (
                    <Typography variant="body2" color="text.secondary">
                        Allocation method:{" "}
                        {METHOD_LABELS[conversion.cost_allocation_method] ||
                            conversion.cost_allocation_method}
                    </Typography>
                )}
                {conversion.notes && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        {conversion.notes}
                    </Typography>
                )}
            </Box>

            <Box>
                <Typography variant="subtitle1" gutterBottom>
                    Inputs consumed
                </Typography>
                {renderTable(inputs, false)}
            </Box>

            <Box>
                <Typography variant="subtitle1" gutterBottom>
                    Outputs produced
                </Typography>
                {renderTable(outputs, conversion.cost_allocation_method === "MANUAL")}
            </Box>

            <Divider />

            <Box display="flex" flexDirection="column" gap={0.5}>
                <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Input cost:</Typography>
                    <Typography variant="body2">
                        {formatCurrencyBDT(inputTotal)}
                    </Typography>
                </Box>
                {conversion.additional_cost > 0 && (
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Additional cost:</Typography>
                        <Typography variant="body2">
                            {formatCurrencyBDT(conversion.additional_cost)}
                        </Typography>
                    </Box>
                )}
                <Box display="flex" justifyContent="space-between">
                    <Typography variant="subtitle1" fontWeight="medium">
                        Total allocated to outputs:
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="medium" color="primary">
                        {formatCurrencyBDT(outputTotal)}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};
